import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import CountryCallingCodePicker from './CountryCallingCodePicker';
import {
  assembleE164,
  COUNTRIES_BY_ISO,
  flagUrl,
  parseInternationalNumber,
  resolveInitialPhoneFields,
} from './countries';
import styles from './PhoneVerificationModal.module.scss';

/**
 * Flag country picker + national number field used by Settings (and reusable).
 *
 * Keeps displayed national digits local. onChange reports a complete E.164 when
 * the national number is valid, otherwise a dial+digits draft (so Settings can
 * normalise on save) or '' when empty / invalid-international.
 *
 * Uses the popover picker (sheet mode is OTP-modal-specific).
 */
const PhoneNumberField = ({
  user,
  value,
  onChange,
  hasError,
  disabled,
  id,
  label,
}) => {
  // Prefer the controlled value for phone digits (including '') so a remount
  // after an intentional clear does not re-prefill from user.phone.
  const initial = resolveInitialPhoneFields({
    phone: value || '',
    location: user && user.location,
  });
  const suggestedIso = initial.iso;
  const [iso, setIso] = useState(initial.iso);
  const [phone, setPhone] = useState(initial.phone);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // After an unrecognised "+" paste, retained digits must not be treated as a
  // national number until the field is cleared or a country is chosen.
  const [invalidInternational, setInvalidInternational] = useState(false);

  // Sync display from a parent international value. Do not clear local digits
  // when value becomes '' — that also happens for invalid-international pastes.
  useEffect(() => {
    if (!value) return;
    const hit = parseInternationalNumber(value);
    if (!hit) return;
    setInvalidInternational(false);
    setIso(hit.iso);
    setPhone(hit.national || '');
  }, [value]);

  const country = COUNTRIES_BY_ISO[iso] || COUNTRIES_BY_ISO.GB;
  const dialText = `+${country.dial}`;

  const emitChange = (nextIso, nextPhone) => {
    if (!onChange) return;
    const assembled = assembleE164(nextIso, nextPhone);
    if (assembled) {
      onChange(assembled);
      return;
    }
    const digits = String(nextPhone || '').replace(/\D/g, '');
    if (!digits) {
      onChange('');
      return;
    }
    const nextCountry = COUNTRIES_BY_ISO[nextIso] || COUNTRIES_BY_ISO.GB;
    const national =
      digits.length > 1 && digits.charAt(0) === '0' ? digits.slice(1) : digits;
    if (!national) {
      onChange('');
      return;
    }
    // Incomplete draft so the parent retains what the user typed for save-time
    // normalisation (matches legacy react-telephone-input behaviour).
    onChange(`+${nextCountry.dial}${national}`);
  };

  const handlePhoneChange = (e) => {
    const v = e.target.value;
    if (v.trim().startsWith('+')) {
      const hit = parseInternationalNumber(v);
      if (hit) {
        setInvalidInternational(false);
        setIso(hit.iso);
        setPhone(hit.national);
        emitChange(hit.iso, hit.national);
        return;
      }
      // Unrecognised international paste: keep sanitised digits visible, but
      // do not treat dial-code digits as a national number for the current ISO.
      // Emit those digits so the parent can reject them; only a real empty
      // input uses the clear sentinel.
      const next = v.replace(/[^\d\s]/g, '');
      setPhone(next);
      setInvalidInternational(!!next);
      if (next && onChange) {
        onChange(next);
      } else {
        emitChange(iso, '');
      }
      return;
    }
    const next = v.replace(/[^\d\s]/g, '');
    setPhone(next);
    if (!next) {
      setInvalidInternational(false);
      emitChange(iso, '');
      return;
    }
    if (invalidInternational) {
      if (onChange) onChange(next);
      return;
    }
    emitChange(iso, next);
  };

  const selectCountry = (nextIso) => {
    setIso(nextIso);
    setInvalidInternational(false);
    setPickerOpen(false);
    setActiveIndex(0);
    emitChange(nextIso, phone);
  };

  return (
    <div className={styles.root}>
      <div className={styles.field}>
        {label ? (
          <label className={styles.label} htmlFor={id}>
            {label}
          </label>
        ) : null}
        <div className={styles.phoneRow}>
          <button
            type="button"
            className={styles.flagTrigger}
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            aria-label={`Country: ${country.name} ${dialText}`}
            disabled={disabled}
            onClick={() => {
              setPickerOpen((o) => !o);
              setActiveIndex(0);
            }}
          >
            <img src={flagUrl(country.iso)} alt="" className={styles.flag} />
            <ChevronDown size={16} strokeWidth={2.2} />
          </button>
          <span
            className={`${styles.phoneWrap} ${
              hasError ? styles.phoneWrapError : ''
            }`}
          >
            <span className={styles.dialPrefix}>{dialText}</span>
            <input
              id={id}
              className={styles.phoneInput}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="7700 900123"
              value={phone}
              disabled={disabled}
              onChange={handlePhoneChange}
              aria-invalid={!!hasError}
            />
          </span>
          <CountryCallingCodePicker
            open={pickerOpen}
            mode="popover"
            selectedIso={iso}
            suggestedIso={suggestedIso}
            onSelect={selectCountry}
            onClose={() => {
              setPickerOpen(false);
              setActiveIndex(0);
            }}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
          />
        </div>
      </div>
    </div>
  );
};

PhoneNumberField.propTypes = {
  user: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func,
  hasError: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
};

PhoneNumberField.defaultProps = {
  user: null,
  value: '',
  onChange: undefined,
  hasError: false,
  disabled: false,
  id: 'phone-number',
  label: undefined,
};

export default PhoneNumberField;
